import React, { useEffect, useState } from 'react';
import { Phone, Mail, MapPin, Clock, ArrowRight, CheckCircle2, MessageSquare, Car, Bus, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import { brand } from '../mock';
import { useToast } from '../hooks/use-toast';
import { createContact, readableError } from '../lib/api';

const Contact = () => {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    reason: 'General enquiry',
    message: '',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (e.target.classList.add('in'), io.unobserve(e.target))),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.reveal').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Please complete required fields', description: 'Name, email and message are required.' });
      return;
    }
    try {
      setSubmitting(true);
      await createContact(form);
      setSubmitted(true);
      toast({ title: 'Message received', description: 'Our reception will be in touch within one business day.' });
    } catch (err) {
      toast({ title: 'Unable to send', description: readableError(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-cream min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-14 lg:pt-40 lg:pb-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-3xl reveal">
            <div className="text-xs uppercase tracking-[0.24em] text-[var(--brand-gold)] mb-4">Get in touch</div>
            <h1 className="font-serif text-5xl lg:text-7xl text-green tracking-tight leading-[1.02]">
              Let’s talk about <em className="italic">your health.</em>
            </h1>
            <p className="mt-6 text-[var(--brand-mute)] text-lg max-w-2xl">
              Reception is available Tuesday to Friday, 9:00am – 5:00pm (Perth time). Call, email or send a message — our team will respond within one business day.
            </p>
          </div>
        </div>
      </section>

      {/* Contact cards */}
      <section className="pb-14">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid md:grid-cols-3 gap-4">
          <ContactCard
            icon={Phone}
            eyebrow="Call reception"
            title={brand.phone}
            desc="For new patient enquiries, bookings and general questions."
            href={`tel:${brand.phone.replace(/\s/g, '')}`}
            cta="Call now"
          />
          <ContactCard
            icon={Mail}
            eyebrow="Send an email"
            title={brand.email}
            desc="For non-urgent enquiries. We respond within one business day."
            href={`mailto:${brand.email}`}
            cta="Email us"
            small
          />
          <ContactCard
            icon={MapPin}
            eyebrow="Visit the clinic"
            title="Mount Lawley, WA"
            desc={brand.address}
            href="https://www.google.com/maps/search/?api=1&query=Unit+6+689+Beaufort+Street+Mount+Lawley+WA+6050"
            cta="Get directions"
            external
          />
        </div>
      </section>

      {/* Map + Hours */}
      <section className="pb-20 lg:pb-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 reveal rounded-[28px] overflow-hidden border border-[rgba(31,58,46,0.1)] bg-[var(--brand-cream-2)] min-h-[420px]">
            <iframe
              title="Elevate Medical Clinic location"
              src={brand.mapEmbed}
              className="w-full h-full min-h-[420px] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          <div className="lg:col-span-4 reveal rounded-[28px] p-8 border border-[rgba(31,58,46,0.1)] bg-[var(--brand-green)] text-[var(--brand-cream)]">
            <div className="h-11 w-11 rounded-full grid place-items-center bg-[var(--brand-cream)] text-[var(--brand-green)] mb-5">
              <Clock size={18} strokeWidth={1.6} />
            </div>
            <div className="text-xs uppercase tracking-[0.22em] text-[var(--brand-gold-2)] mb-3">Opening hours</div>
            <div className="font-serif text-2xl mb-6">Perth time (AWST)</div>
            <div className="divide-y divide-[rgba(247,243,236,0.12)]">
              {brand.hours.map(([d, t]) => (
                <div key={d} className="flex items-center justify-between py-3 text-[15px]">
                  <span className="opacity-90">{d}</span>
                  <span className={`font-medium ${t === 'Closed' ? 'opacity-60' : 'text-[var(--brand-gold-2)]'}`}>{t}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-5 border-t border-[rgba(247,243,236,0.15)] flex items-start gap-3">
              <Car size={16} className="mt-1 text-[var(--brand-gold-2)] shrink-0" />
              <div className="text-sm opacity-85">On-street parking available along Beaufort Street.</div>
            </div>
            <div className="mt-3 flex items-start gap-3">
              <Bus size={16} className="mt-1 text-[var(--brand-gold-2)] shrink-0" />
              <div className="text-sm opacity-85">Transperth 950 & 60 stop nearby on Beaufort Street.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section className="pb-20 lg:pb-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="rounded-[32px] overflow-hidden border border-[rgba(31,58,46,0.1)] bg-[var(--brand-cream-2)] grid lg:grid-cols-12">
            <div className="lg:col-span-5 p-8 lg:p-12 border-b lg:border-b-0 lg:border-r border-[rgba(31,58,46,0.1)]">
              <div className="h-11 w-11 rounded-full grid place-items-center bg-[var(--brand-green)] text-[var(--brand-cream)] mb-5">
                <MessageSquare size={18} strokeWidth={1.6} />
              </div>
              <div className="text-xs uppercase tracking-[0.24em] text-[var(--brand-gold)] mb-4">Send a message</div>
              <h2 className="font-serif text-4xl lg:text-5xl text-green leading-[1.05]">
                Prefer to <em className="italic">write?</em>
              </h2>
              <p className="mt-5 text-[var(--brand-mute)] max-w-md">
                Send us the details of your enquiry and reception will respond within one business day. For urgent medical concerns, please call the clinic directly.
              </p>

              <div className="mt-8 space-y-3 text-sm text-[var(--brand-ink)]">
                <div className="flex items-center gap-3"><Phone size={14} className="text-[var(--brand-gold)]" /> <a className="hover:text-[var(--brand-gold)]" href={`tel:${brand.phone.replace(/\s/g, '')}`}>{brand.phone}</a></div>
                <div className="flex items-center gap-3"><Mail size={14} className="text-[var(--brand-gold)]" /> <a className="hover:text-[var(--brand-gold)] break-all" href={`mailto:${brand.email}`}>{brand.email}</a></div>
                <div className="flex items-start gap-3"><MapPin size={14} className="text-[var(--brand-gold)] mt-1" /> <span>{brand.address}</span></div>
              </div>
            </div>

            <div className="lg:col-span-7 p-8 lg:p-12">
              {submitted ? (
                <div className="h-full flex flex-col justify-center items-start">
                  <div className="h-14 w-14 rounded-full bg-[var(--brand-green)] text-[var(--brand-cream)] grid place-items-center">
                    <CheckCircle2 size={26} />
                  </div>
                  <div className="font-serif text-3xl mt-5 text-green">Thank you, {form.name}.</div>
                  <p className="mt-3 text-[var(--brand-mute)] max-w-md">
                    Your message has been received. A member of our reception team will reach out within one business day.
                  </p>
                  <div className="mt-8 flex gap-3">
                    <button
                      onClick={() => {
                        setSubmitted(false);
                        setForm({ name: '', email: '', phone: '', reason: 'General enquiry', message: '' });
                      }}
                      className="btn-outline text-sm"
                    >
                      Send another message
                    </button>
                    <Link to="/" className="btn-primary text-sm">Back to home <ArrowRight size={16} /></Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-4">
                  <Field label="Your name*" v={form.name} onChange={(v) => update('name', v)} />
                  <Field label="Phone" type="tel" v={form.phone} onChange={(v) => update('phone', v)} />
                  <div className="sm:col-span-2">
                    <Field label="Email*" type="email" v={form.email} onChange={(v) => update('email', v)} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Reason for contact</Label>
                    <select
                      value={form.reason}
                      onChange={(e) => update('reason', e.target.value)}
                      className="w-full bg-[var(--brand-cream)] border border-[rgba(31,58,46,0.15)] rounded-xl px-4 py-3.5 text-green focus:outline-none focus:border-[var(--brand-green)]"
                    >
                      {[
                        'General enquiry',
                        'New patient booking',
                        'Existing patient question',
                        'Dietitian consult',
                        'Cosmetic / nurse consult',
                        'Billing / accounts',
                        'Other',
                      ].map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Message*</Label>
                    <textarea
                      rows={5}
                      value={form.message}
                      onChange={(e) => update('message', e.target.value)}
                      placeholder="Briefly tell us how we can help…"
                      className="w-full bg-[var(--brand-cream)] border border-[rgba(31,58,46,0.15)] rounded-xl px-4 py-3.5 text-green placeholder:text-[var(--brand-mute)] focus:outline-none focus:border-[var(--brand-green)] resize-none"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <button type="submit" disabled={submitting} className="btn-primary text-sm disabled:opacity-70">
                      {submitting ? (<><Loader2 size={16} className="animate-spin" /> Sending…</>) : (<>Send message <ArrowRight size={16} /></>)}
                    </button>
                    <p className="mt-3 text-[12px] text-[var(--brand-mute)]">
                      By submitting, you agree to be contacted regarding your enquiry. This form is not for medical advice or emergencies.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Billing / policies */}
      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid md:grid-cols-3 gap-4">
          <PolicyCard
            title="Private billing practice"
            desc="Elevate is a private billing clinic. Fees are quoted at the time of booking. Payment is required at consultation."
          />
          <PolicyCard
            title="Cancellation policy"
            desc="7+ days notice: no fee. Less than 48 hours or no-show: 100% of the consultation fee."
          />
          <PolicyCard
            title="New patient wait-list"
            desc="A wait-list currently applies for new patients wanting to see Dr Chaffee. Please call reception to discuss."
          />
        </div>
      </section>

      <Footer />
    </div>
  );
};

const ContactCard = ({ icon: Ic, eyebrow, title, desc, href, cta, external, small }) => (
  <a
    href={href}
    target={external ? '_blank' : undefined}
    rel={external ? 'noreferrer' : undefined}
    className="reveal card-hover rounded-2xl p-7 border border-[rgba(31,58,46,0.08)] bg-[var(--brand-cream-2)] group flex flex-col h-full"
  >
    <div className="h-11 w-11 rounded-full grid place-items-center bg-[var(--brand-green)] text-[var(--brand-cream)] mb-5">
      <Ic size={18} strokeWidth={1.6} />
    </div>
    <div className="text-xs uppercase tracking-[0.22em] text-[var(--brand-gold)]">{eyebrow}</div>
    <div className={`font-serif text-green leading-tight mt-2 ${small ? 'text-lg break-all' : 'text-2xl'}`}>{title}</div>
    <p className="mt-3 text-sm text-[var(--brand-mute)] leading-relaxed flex-1">{desc}</p>
    <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-green group-hover:text-[var(--brand-gold)] transition-colors">
      {cta} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
    </div>
  </a>
);

const PolicyCard = ({ title, desc }) => (
  <div className="reveal rounded-2xl p-6 border border-[rgba(31,58,46,0.08)] bg-[var(--brand-cream-2)]">
    <div className="font-serif text-xl text-green leading-tight">{title}</div>
    <p className="mt-2 text-sm text-[var(--brand-mute)] leading-relaxed">{desc}</p>
  </div>
);

const Label = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--brand-gold)] mb-2">{children}</div>
);

const Field = ({ label, v, onChange, type = 'text' }) => (
  <div>
    <Label>{label}</Label>
    <input
      type={type}
      value={v}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[var(--brand-cream)] border border-[rgba(31,58,46,0.15)] rounded-xl px-4 py-3.5 text-green placeholder:text-[var(--brand-mute)] focus:outline-none focus:border-[var(--brand-green)]"
    />
  </div>
);

export default Contact;
